import { apiClient, newIdempotencyKey } from './client'
import { ApiError, type ProblemDetail } from './problem'
import { oidcUserManager } from '../auth/oidcUserManager'
import type { CursorPage } from './accounts'
import type {
  EvidenceFileView,
  EvidenceItemView,
  EvidenceLinkView,
  EvidenceRequirementView,
  EvidenceUploadResult,
  ReadinessScoreView,
} from '../types/evidence'

/**
 * Typed fetch wrappers for `module.evidence`'s real vault surface (`EvidenceItemController`/
 * `EvidenceLinkController`/`EvidenceRequirementController`, evidence-vault story M14, IB-167) —
 * same thin convention as every other `api/*.ts` file. `uploadEvidenceItem`/`downloadEvidenceFile`
 * cannot use the shared `apiClient` (JSON-only in and out): they carry a multipart request /
 * binary response, so they build their own request, mirroring `apiClient`'s own bearer-token and
 * RFC 9457 problem-body handling rather than duplicating it silently differently — the same
 * convention `api/spendImport.ts`'s `uploadSpendImportBatch` already established.
 */

const API_BASE = '/api/v1'

async function authHeader(): Promise<HeadersInit> {
  const user = await oidcUserManager.getUser()
  return user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {}
}

async function toApiError(res: Response): Promise<ApiError> {
  let problem: ProblemDetail
  try {
    const body = (await res.json()) as Partial<ProblemDetail>
    problem = { status: res.status, ...body }
  } catch {
    problem = { status: res.status, title: res.statusText }
  }
  return new ApiError(problem)
}

/**
 * `GET /cases/{caseId}/evidence-items` (`EvidenceItemController.list`) — the Case-workspace
 * Evidence tab's list.
 */
export async function listEvidenceItems(
  caseId: string,
  signal?: AbortSignal,
): Promise<CursorPage<EvidenceItemView>> {
  const { data } = await apiClient.get<CursorPage<EvidenceItemView>>(`/cases/${caseId}/evidence-items`, { signal })
  return data
}

/**
 * `POST /cases/{caseId}/evidence-items/upload` (multipart, `EvidenceItemController.upload`) — real
 * object-store byte intake (FR-M14.2): scan (quarantine-on-fail) -&gt; server-compute sha256 -&gt;
 * store (clean only) -&gt; persist. No `Idempotency-Key`: this route is under `/api/v1/cases/*`,
 * which `IdempotencyFilterConfig` deliberately does not cover yet (documented IB-52 follow-up,
 * same reason `uploadSpendImportBatch` sends none either).
 */
export async function uploadEvidenceItem(
  caseId: string,
  fields: { type: string; contemporaneous: boolean; region: string; retentionPolicy: string },
  file: File,
): Promise<EvidenceUploadResult> {
  const form = new FormData()
  form.append('file', file)
  form.append('type', fields.type)
  form.append('contemporaneous', String(fields.contemporaneous))
  form.append('region', fields.region)
  form.append('retentionPolicy', fields.retentionPolicy)

  const res = await fetch(`${API_BASE}/cases/${caseId}/evidence-items/upload`, {
    method: 'POST',
    headers: await authHeader(),
    body: form,
  })
  if (!res.ok) {
    throw await toApiError(res)
  }
  return (await res.json()) as EvidenceUploadResult
}

/**
 * `POST /evidence-items/{id}/promote` — the one-way `draft -&gt; accepted` transition
 * (`EvidenceItemController.promote`). Refuses a quarantined item `409` (never promotable). Carries
 * a fresh `Idempotency-Key` — this route is under `/api/v1/evidence-items/*`, one of
 * `IdempotencyFilterConfig`'s covered creating-POST prefixes.
 */
export async function promoteEvidenceItem(itemId: string): Promise<EvidenceItemView> {
  const { data } = await apiClient.post<EvidenceItemView>(`/evidence-items/${itemId}/promote`, undefined, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

/**
 * `POST /evidence-items/{itemId}/links` with `targetType: "requirement_instance"` — satisfies the
 * named `EvidenceRequirement` the moment an ACCEPTED `EvidenceItem` is linked to it (FR-M14.3).
 * Same `/api/v1/evidence-items/*` idempotency coverage as {@link promoteEvidenceItem}.
 */
export async function linkEvidenceItemToRequirement(
  evidenceItemId: string,
  requirementId: string,
): Promise<EvidenceLinkView> {
  const { data } = await apiClient.post<EvidenceLinkView>(
    `/evidence-items/${evidenceItemId}/links`,
    { targetType: 'requirement_instance', targetId: requirementId },
    { idempotencyKey: newIdempotencyKey() },
  )
  return data
}

/** `GET /evidence-items/{id}/file-meta` — the `EvidenceFile` row (AV scan status, hash, size), no bytes. */
export async function getEvidenceFileMeta(itemId: string, signal?: AbortSignal): Promise<EvidenceFileView> {
  const { data } = await apiClient.get<EvidenceFileView>(`/evidence-items/${itemId}/file-meta`, { signal })
  return data
}

/**
 * `GET /evidence-items/{id}/file` — streams the stored bytes back (a sensitive read, audited
 * server-side even on success). Raw `fetch`, not `apiClient` (binary body, not JSON); the filename
 * is read back from the same `Content-Disposition` header the server set
 * (`EvidenceItemController.file`). `404` for a metadata-only item, `409` for a quarantined one —
 * both surface as an {@link ApiError} the caller renders honestly rather than a silent no-op.
 */
export async function downloadEvidenceFile(
  itemId: string,
): Promise<{ blob: Blob; filename: string; contentType: string }> {
  const res = await fetch(`${API_BASE}/evidence-items/${itemId}/file`, {
    method: 'GET',
    headers: await authHeader(),
  })
  if (!res.ok) {
    throw await toApiError(res)
  }
  const contentType = res.headers.get('Content-Type') ?? 'application/octet-stream'
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(disposition)
  const filename = match?.[1] ?? 'evidence-file'
  const blob = await res.blob()
  return { blob, filename, contentType }
}

/**
 * `GET /program-filings/{filingId}/evidence-requirements` (`EvidenceRequirementController.list`) —
 * no cursor (low cardinality per filing, mirrors `EvidenceLinkController`'s by-item list).
 */
export async function listEvidenceRequirements(
  programFilingId: string,
  signal?: AbortSignal,
): Promise<EvidenceRequirementView[]> {
  const { data } = await apiClient.get<EvidenceRequirementView[]>(
    `/program-filings/${programFilingId}/evidence-requirements`,
    { signal },
  )
  return data
}

/**
 * `POST /evidence-requirements/{id}/waive` (FR-M14.5) — a mandatory, &ge;10-character reason
 * (server-validated; this call does not duplicate that check client-side beyond disabling submit).
 * No `Idempotency-Key`: `/api/v1/evidence-requirements/*` is not one of
 * `IdempotencyFilterConfig`'s covered prefixes (mirrors `EvidenceVaultIntegrationTest`'s own call).
 */
export async function waiveEvidenceRequirement(
  requirementId: string,
  reason: string,
): Promise<EvidenceRequirementView> {
  const { data } = await apiClient.post<EvidenceRequirementView>(`/evidence-requirements/${requirementId}/waive`, {
    reason,
  })
  return data
}

/**
 * `GET /api/v1/program-filings/{filingId}/readiness-score` (`ReadinessScoreController.get`, IB-16,
 * ADR-0028 decision (f)) — the last persisted, self-dated (`computedAt`) score. Returns `null`
 * (never throws) on a `404` — "never computed yet" is a legitimate, common state (IB-282 View 2
 * AC2.3's own documented "Not yet scored" tile state), not a fetch error; any other failure still
 * propagates as `ApiError`.
 */
export async function getReadinessScore(
  programFilingId: string,
  signal?: AbortSignal,
): Promise<ReadinessScoreView | null> {
  try {
    const { data } = await apiClient.get<ReadinessScoreView>(`/program-filings/${programFilingId}/readiness-score`, {
      signal,
    })
    return data
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null
    }
    throw err
  }
}

/**
 * `POST /api/v1/program-filings/{filingId}/readiness-score` (`ReadinessScoreController.compute`) —
 * the "Compute now" action (IB-282 View 2 AC2.3's empty-state CTA, never a dead button). A mutation
 * (recomputes + upserts + returns), audited server-side; idempotent-in-effect (repeat calls just
 * recompute the same deterministic proxy over current data), so this wrapper sends no
 * `Idempotency-Key` — mirrors `finalizeComputationRun`'s own reasoning for a route outside
 * `IdempotencyFilterConfig`'s covered prefixes.
 */
export async function computeReadinessScore(programFilingId: string): Promise<ReadinessScoreView> {
  const { data } = await apiClient.post<ReadinessScoreView>(
    `/program-filings/${programFilingId}/readiness-score`,
    undefined,
  )
  return data
}
